import { motion, AnimatePresence } from 'framer-motion';
import { X, Keyboard } from 'lucide-react';
import { type ShortcutConfig, formatShortcut } from '@/hooks/useKeyboardShortcuts';

interface ShortcutsHelpModalProps {
  open: boolean;
  onClose: () => void;
  shortcuts: {
    navigation: ShortcutConfig[];
    actions: ShortcutConfig[];
    general: ShortcutConfig[];
  };
}

export function ShortcutsHelpModal({ open, onClose, shortcuts }: ShortcutsHelpModalProps) {
  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg z-50"
          >
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Keyboard className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Raccourcis clavier
                  </h2>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 max-h-[60vh] overflow-y-auto">
                <div className="grid gap-6">
                  {/* General */}
                  {shortcuts.general.length > 0 && (
                    <ShortcutGroup title="Général" shortcuts={shortcuts.general} />
                  )}

                  {/* Navigation */}
                  {shortcuts.navigation.length > 0 && (
                    <ShortcutGroup title="Navigation" shortcuts={shortcuts.navigation} />
                  )}

                  {/* Actions */}
                  {shortcuts.actions.length > 0 && (
                    <ShortcutGroup title="Actions" shortcuts={shortcuts.actions} />
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                  Appuyez sur <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs font-mono">?</kbd> pour afficher cette aide
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function ShortcutGroup({
  title,
  shortcuts,
}: {
  title: string;
  shortcuts: ShortcutConfig[];
}) {
  // Filter out duplicate descriptions (like two "Rétablir" shortcuts)
  const uniqueShortcuts = shortcuts.reduce<ShortcutConfig[]>((acc, shortcut) => {
    if (!acc.find(s => s.description === shortcut.description)) {
      acc.push(shortcut);
    }
    return acc;
  }, []);

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 uppercase tracking-wider">
        {title}
      </h3>
      <div className="space-y-2">
        {uniqueShortcuts.map((shortcut, index) => (
          <ShortcutRow key={index} shortcut={shortcut} />
        ))}
      </div>
    </div>
  );
}

function ShortcutRow({ shortcut }: { shortcut: ShortcutConfig }) {
  const formattedShortcut = formatShortcut(shortcut);
  const keys = formattedShortcut.split('+');

  return (
    <div className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
      <span className="text-gray-700 dark:text-gray-300">{shortcut.description}</span>
      <div className="flex items-center gap-1">
        {keys.map((key, index) => (
          <span key={index} className="flex items-center gap-1">
            <kbd className="min-w-[24px] px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded shadow-sm text-center">
              {key}
            </kbd>
            {index < keys.length - 1 && (
              <span className="text-gray-400 text-xs">+</span>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

export default ShortcutsHelpModal;
